import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { formatDateDb } from "../utils/tools";

// Chấm công luồng mới - Bước 1: đăng ký thiết bị + địa điểm chấm công
const NUMBER_PER_PAGE = 25;

const placeName = (place) => place?.identifier || place?.name || "-";

const deviceName = (info) =>
  info?.modelName || info?.deviceName || info?.model || "-";

const buildPaging = (data) => {
  const total = data?.pagination?.total || 0;
  const page = data?.pagination?.page || 1;
  const limit = data?.pagination?.limit || NUMBER_PER_PAGE;
  return {
    totalPages: Math.ceil(total / NUMBER_PER_PAGE),
    entries: total,
    startEntry: total ? (page - 1) * limit + 1 : 0,
    endEntry: Math.min(page * limit, total),
  };
};

async function fetchRegistrations(currentPage, search) {
  try {
    const options = { limit: NUMBER_PER_PAGE, page: currentPage };
    if (search) options.search = search;

    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/checkinV2/registrations?${queryParams}`,
    );

    const newData = (data?.items || []).map((item) => ({
      id: item?._id || "",
      code: item?.employeeObject?.code || "-",
      name: item?.employeeObject?.fullName || "-",
      deviceCode: item?.deviceCode || "-",
      deviceName: deviceName(item?.deviceInfo),
      appVersion: item?.appVersion || "-",
      place: placeName(item?.checkinPlace),
      registeredAt: item?.registeredAt ? formatDateDb(item.registeredAt) : "-",
      action: item?._id || "",
    }));

    return { data: newData, ...buildPaging(data) };
  } catch (error) {
    console.error("Error fetching checkin registrations:", error);
    return { data: [], totalPages: 0 };
  }
}

async function fetchRegistrationRequests(currentPage, search, status) {
  try {
    const options = { limit: NUMBER_PER_PAGE, page: currentPage };
    if (search) options.search = search;
    if (status) options.status = status;

    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/checkinV2/change-requests?${queryParams}`,
    );

    const newData = (data?.items || []).map((item) => {
      // Máy trong đơn đang đăng ký cho NV khác lúc gửi đơn => duyệt sẽ chuyển máy
      const deviceOwner = item?.deviceOwnerObject
        ? [item.deviceOwnerObject.code, item.deviceOwnerObject.fullName]
            .filter(Boolean)
            .join(" - ")
        : "";
      return {
        id: item?._id || "",
        code: item?.employeeObject?.code || "-",
        name: item?.employeeObject?.fullName || "-",
        status: item?.status || "-",
        reason: item?.reason || "-",
        deviceOwner,
        device: item?.requestedDeviceCode
          ? `${item?.currentDeviceCode || "Chưa đăng ký"} → ${item.requestedDeviceCode} (${deviceName(
              item?.requestedDeviceInfo,
            )})${deviceOwner ? ` · Máy đang đăng ký cho ${deviceOwner}, duyệt sẽ chuyển máy` : ""}`
          : "Không đổi",
        place: item?.requestedCheckinPlaceId
          ? `${placeName(item?.currentCheckinPlace)} → ${placeName(
              item?.requestedCheckinPlace,
            )}`
          : "Không đổi",
        createdAt: item?.createdAt ? formatDateDb(item.createdAt) : "-",
        handler: item?.handler?.fullName
          ? `${item.handler.fullName}${item?.adminNote ? `: ${item.adminNote}` : ""}`
          : "-",
      };
    });

    return { data: newData, ...buildPaging(data) };
  } catch (error) {
    console.error("Error fetching checkin registration requests:", error);
    return { data: [], totalPages: 0 };
  }
}

const HISTORY_ACTION_LABEL = {
  REGISTER: "Đăng ký lần đầu",
  CHANGE: "Đổi (duyệt đơn)",
  DELETE: "Xoá đăng ký",
};

const fromTo = (from, to) => {
  if (from && to && from !== to) return `${from} → ${to}`;
  return to || from || "-";
};

async function fetchRegistrationHistory(currentPage, search) {
  try {
    const options = { limit: NUMBER_PER_PAGE, page: currentPage };
    if (search) options.search = search;

    const queryParams = new URLSearchParams(options).toString();
    const data = await clientAPI(
      "get",
      `/checkinV2/registrations/history?${queryParams}`,
    );

    const newData = (data?.items || []).map((item) => ({
      id: item?._id || "",
      createdAt: item?.createdAt ? formatDateDb(item.createdAt) : "-",
      code: item?.employeeObject?.code || "-",
      name: item?.employeeObject?.fullName || "-",
      action:
        item?.action === "REGISTER" && item?.requestId
          ? "Đăng ký (duyệt đơn)"
          : HISTORY_ACTION_LABEL[item?.action] || item?.action || "-",
      device: fromTo(
        item?.previousDeviceCode &&
          `${item.previousDeviceCode} (${deviceName(item?.previousDeviceInfo)})`,
        item?.deviceCode &&
          `${item.deviceCode} (${deviceName(item?.deviceInfo)})`,
      ),
      place: fromTo(
        item?.previousCheckinPlaceId && placeName(item?.previousCheckinPlace),
        item?.checkinPlaceId && placeName(item?.checkinPlace),
      ),
      creator: item?.creator?.fullName || "-",
      reason: item?.reason || "-",
    }));

    return { data: newData, ...buildPaging(data) };
  } catch (error) {
    console.error("Error fetching checkin registration history:", error);
    return { data: [], totalPages: 0 };
  }
}

const usePagedQuery = (key, deps, fn) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [key, currentPage, ...deps],
    queryFn: () => fn(currentPage),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
  });

  return {
    items: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
};

export function useCheckinRegistrations(search) {
  return usePagedQuery("checkinRegistrations", [search], (page) =>
    fetchRegistrations(page, search),
  );
}

export function useCheckinRegistrationHistory(search) {
  return usePagedQuery("checkinRegistrationHistory", [search], (page) =>
    fetchRegistrationHistory(page, search),
  );
}

export function useCheckinRegistrationRequests(search, status) {
  return usePagedQuery("checkinRegistrationRequests", [search, status], (page) =>
    fetchRegistrationRequests(page, search, status),
  );
}
